import React, { useEffect, useState, useContext } from 'react';
import { Box, SpaceBetween, Modal, FormField, Button, Input, Alert } from '@amzn/awsui-components-react';
import { Autosuggest } from '@amzn/awsui-components-react/polaris';
import StringUtils from 'src/utils/stringUtils';
import { GlobalAppContext } from '../App';

export interface CloneWorksheetRequest {
    sourceWorksheetId: string;
    worksheetName: string;
    providerCompanyCode: string;
    sheetVersion: number;
}

interface CreateWorksheetModalProps {
    visible: boolean;
    isCreating: boolean;
    createError: string;
    worksheetId?: string;
    worksheetVersion?: number;
    onClose: () => void;
    onSubmit: (request: CloneWorksheetRequest) => void;
}

/**
 * Modal component for cloning multidimensional allocation worksheets
 * Provides form validation and error handling for worksheet cloning
 */
export default function CreateWorksheetModal(props: CreateWorksheetModalProps) {
    const { visible, isCreating, createError, worksheetId, worksheetVersion, onClose, onSubmit } = props;
    const { services } = useContext(GlobalAppContext);

    const [worksheetName, setWorksheetName] = useState('');
    const [providerCompanyCode, setProviderCompanyCode] = useState('');
    const [nameValidationError, setNameValidationError] = useState('');
    const [companyCodeValidationError, setCompanyCodeValidationError] = useState('');
    const [companyOptions, setCompanyOptions] = useState<Array<{value: string}>>([]);
    const [companyLOV, setCompanyLOV] = useState<string[]>([]);

    // Fetch company LOV when modal opens
    const [companyLOVData] = services.dataSourcesService.getCoaData(
        visible ? 'companies' : ''
    );

    // Update companyLOV state when data is fetched
    useEffect(() => {
        if (companyLOVData) {
            setCompanyLOV(companyLOVData);
        }
    }, [companyLOVData]);

    // Reset form when modal opens
    useEffect(() => {
        if (visible) {
            setWorksheetName('');
            setProviderCompanyCode('');
            setNameValidationError('');
            setCompanyCodeValidationError('');
        }
    }, [visible]);

    // Worksheet name validation rules
    const validateWorksheetName = (name: string): string => {
        if (StringUtils.isNullOrEmpty(name)) {
            return 'Worksheet name is required';
        }

        // Character validation - allow letters, numbers, spaces, hyphens, underscores, parentheses
        const validCharacterPattern = /^[a-zA-Z0-9\s\-_()]+$/;
        if (!validCharacterPattern.test(name)) {
            return 'Worksheet name can only contain letters, numbers, spaces, hyphens, underscores, and parentheses';
        }

        // Cannot be only spaces or special characters
        if (!/[a-zA-Z0-9]/.test(name)) {
            return 'Worksheet name must contain at least one letter or number';
        }

        return '';
    };

    // Company code validation rules
    const validateCompanyCode = (code: string): string => {
        if (StringUtils.isNullOrEmpty(code)) {
            return 'Provider company code is required';
        }

        // Basic alphanumeric validation
        const validPattern = /^[a-zA-Z0-9\-_]+$/;
        if (!validPattern.test(code)) {
            return 'Company code can only contain letters, numbers, hyphens, and underscores';
        }

        return '';
    };

    // Handle input change with immediate validation
    const handleNameChange = (newName: string) => {
        const validationError = validateWorksheetName(newName);
        setWorksheetName(newName);
        setNameValidationError(validationError);
    };

    const handleCompanyCodeChange = (newCode: string) => {
        setProviderCompanyCode(newCode);
    };

    const handleCompanyCodeBlur = () => {
        const beautifiedCode = providerCompanyCode.trim();
        setProviderCompanyCode(beautifiedCode);
        const validationError = validateCompanyCode(beautifiedCode);
        setCompanyCodeValidationError(validationError);
    };

    const handleLoadCompanyItems = (event: any) => {
        const { filteringText } = event.detail;

        if (filteringText == null || filteringText === '') {
            setCompanyOptions([]);
            return;
        }

        const filteredOptions = companyLOV
            ?.filter(x => x.toLowerCase().startsWith(filteringText.toLowerCase()))
            .map((val) => ({ value: val }));
        
        setCompanyOptions(filteredOptions || []);
    };

    const isCreateDisabled = 
        StringUtils.isNullOrEmpty(worksheetName) || 
        !StringUtils.isNullOrEmpty(nameValidationError) ||
        StringUtils.isNullOrEmpty(providerCompanyCode) ||
        !StringUtils.isNullOrEmpty(companyCodeValidationError);

    const handleSubmit = () => {
        if (!worksheetId || worksheetVersion === undefined) {
            return;
        }

        const cloneWorksheetRequest: CloneWorksheetRequest = {
            sourceWorksheetId: worksheetId,
            worksheetName: worksheetName,
            providerCompanyCode: providerCompanyCode,
            sheetVersion: worksheetVersion
        };
        onSubmit(cloneWorksheetRequest);
    };

    return (
        <Modal
            data-class="cloneWorksheetModal"
            visible={visible}
            header="Clone worksheet"
            size="medium"
            onDismiss={onClose}
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button 
                            variant="link" 
                            className="cancelButton" 
                            disabled={isCreating} 
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            className="cloneButton" 
                            disabled={isCreating || isCreateDisabled} 
                            onClick={handleSubmit}
                        >
                            {isCreating ? 'Cloning...' : 'Clone'}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween direction="vertical" size="m">
                <Box>
                    Clone the current worksheet with a new name and provider company code.
                </Box>
                <FormField
                    data-class="cloneModalField"
                    label="Cloned worksheet name"
                    stretch={false}
                    description="Enter a unique name for the cloned worksheet"
                    errorText={nameValidationError}
                >
                    <Input
                        data-class="basicModalInput worksheetNameFieldInput"
                        value={worksheetName}
                        onChange={e => handleNameChange(e.detail.value)}
                        placeholder="Enter cloned worksheet name"
                        ariaLabel="Worksheet name input"
                        autoFocus
                        invalid={!StringUtils.isNullOrEmpty(nameValidationError)}
                    />
                </FormField>
                <FormField
                    data-class="cloneModalField"
                    label="Provider company code"
                    stretch={false}
                    description="Select or enter the provider company code for the cloned worksheet"
                    errorText={companyCodeValidationError}
                >
                    <Autosuggest
                        data-class="coaAutosuggest companyCodeFieldInput"
                        onChange={({ detail }) => handleCompanyCodeChange(detail.value)}
                        onBlur={handleCompanyCodeBlur}
                        onSelect={({ detail }) => handleCompanyCodeChange(detail.value)}
                        onLoadItems={handleLoadCompanyItems}
                        value={providerCompanyCode}
                        options={companyOptions}
                        enteredTextLabel={val => `Use: "${val}"`}
                        placeholder="Select or enter a company code"
                        empty="No matches found"
                        loadingText="Loading"
                        ariaLabel="Provider company code input"
                        invalid={!StringUtils.isNullOrEmpty(companyCodeValidationError)}
                    />
                </FormField>
                {createError && !StringUtils.isNullOrEmpty(createError) && (
                    <Alert type="error" dismissible={false}>
                        {createError}
                    </Alert>
                )}
            </SpaceBetween>
        </Modal>
    );
}
